import { useCallback, useEffect, useState } from 'react';
import { errorMessageMap } from '../errors.ts/errorMessageMap';
import { useModelStatus } from './modelStatus';
const urnsMap = {};
export function getViewerUrnByUrn(urn) {
  var _Object$entries$find;

  return (_Object$entries$find = Object.entries(urnsMap).find(([key, value]) => value === urn)) === null || _Object$entries$find === void 0 ? void 0 : _Object$entries$find[0];
}
export function getUrnByViewerUrn(viewerUrn) {
  return urnsMap[viewerUrn];
}
export function setViewerUrnMappingItem(viewerUrn, urn) {
  urnsMap[viewerUrn] = urn;
}
export function useModelManagement({
  models,
  aggregatedView,
  onModelLoaded,
  onLoadModelError,
  onModelUnloaded,
  loadModelByUrl,
  getModelUrl
}) {
  const [geometryLoaded, setGeometryLoaded] = useState(null);
  const [invertedExternalIdMap, setInvertedExternalIdMap] = useState({});
  const [loading, setLoading] = useState(false);
  const onRemoveModel = useCallback(async urns => {
    if (!aggregatedView) {
      return;
    }

    if (loadModelByUrl) {
      const models = aggregatedView.viewer.getVisibleModels();
      models.forEach(model => {
        const data = model.getData();
        const realUrn = getUrnByViewerUrn(data.urn);

        if (!data || !urns.includes(realUrn)) {
          return;
        }

        aggregatedView.viewer.unloadModel(model);
      });
      return;
    }

    const loadedInViewerNodes = await aggregatedView.getVisibleNodes();
    loadedInViewerNodes.forEach(async node => {
      const model = await aggregatedView.getModelAndWait(node);
      const data = model.getData();
      const realUrn = getUrnByViewerUrn(data.urn);

      if (!data || !urns.includes(realUrn)) {
        return;
      }

      aggregatedView.unload(node);
      onModelUnloaded === null || onModelUnloaded === void 0 ? void 0 : onModelUnloaded({
        urn: realUrn
      });
    });
  }, [aggregatedView, loadModelByUrl, onModelUnloaded]);
  const {
    modelStatus,
    onModelGeometryLoaded
  } = useModelStatus(models, onRemoveModel);
  const fetchGeometryData = useCallback(async (model, refetch) => {
    const result = await new Promise((resolve, reject) => model.getExternalIdMapping(resolve, () => {
      // TODO иногда viewer может выдать 'Failed to load properties'
      // Пробуем рефетчить иначе выкидываем ошибку
      // Проверить после рефакторинга filteringTools
      if (!refetch) {
        fetchGeometryData(model, true);
        return;
      }

      onLoadModelError === null || onLoadModelError === void 0 ? void 0 : onLoadModelError({
        urn: getUrnByViewerUrn(model.getData().urn),
        errorMessage: errorMessageMap.LOAD_EXTERNAL_ID_MAPPING
      });
    }));
    const modelData = model.getData();

    if (!modelData) {
      onLoadModelError === null || onLoadModelError === void 0 ? void 0 : onLoadModelError({
        urn: 'Last loaded model',
        errorMessage: errorMessageMap.LOAD_EXTERNAL_ID_MAPPING
      });
      return;
    }

    const realUrn = getUrnByViewerUrn(modelData.urn);
    onModelLoaded({
      urn: realUrn
    });
    setGeometryLoaded(old => ({ ...old,
      [realUrn]: result
    }));
    const invertedMap = {};
    Object.keys(result).forEach(key => {
      invertedMap[result[key]] = key;
    });
    setInvertedExternalIdMap(old => ({ ...old,
      [realUrn]: invertedMap
    }));
  }, [onLoadModelError, onModelLoaded]);
  const onGeometryLoaded = useCallback(({
    model
  }) => {
    fetchGeometryData(model);
    onModelGeometryLoaded(getUrnByViewerUrn(model.getData().urn));
    setLoading(false);
  }, [fetchGeometryData, onModelGeometryLoaded]);
  const modelRemoved = useCallback(({
    model
  }) => {
    const realUrn = getUrnByViewerUrn(model.getData().urn);

    if (geometryLoaded && geometryLoaded[realUrn]) {
      setGeometryLoaded(old => {
        const state = { ...old
        };
        delete state[realUrn];
        return state;
      });
      setInvertedExternalIdMap(old => {
        const state = { ...old
        };
        delete state[realUrn];
        return state;
      });
    }
  }, [geometryLoaded]);
  useEffect(() => {
    if (aggregatedView) {
      aggregatedView.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeometryLoaded);
      aggregatedView.viewer.addEventListener(Autodesk.Viewing.MODEL_REMOVED_EVENT, modelRemoved);
      return () => {
        aggregatedView.viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeometryLoaded);
        aggregatedView.viewer.removeEventListener(Autodesk.Viewing.MODEL_REMOVED_EVENT, modelRemoved);
      };
    }
  }, [aggregatedView, modelRemoved, onGeometryLoaded]);
  const loadModel = useCallback(async (modelUrn, aggregatedView) => {
    setLoading(true);
    const document = await new Promise((resolve, reject) => {
      setViewerUrnMappingItem(modelUrn, modelUrn);
      return Autodesk.Viewing.Document.load(`urn:${modelUrn}`, res => resolve(res), errorCode => {
        setLoading(false);
        onLoadModelError === null || onLoadModelError === void 0 ? void 0 : onLoadModelError({
          urn: modelUrn,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errorMessage: errorMessageMap[errorCode]
        });
      });
    });
    const newNodes = await document.getRoot().search({
      type: 'geometry',
      role: '3d'
    });
    const nodeToLoad = newNodes[0];
    const nodes = [...aggregatedView.getVisibleNodes(), nodeToLoad]; // https://forge.autodesk.com/en/docs/viewer/v7/reference/Viewing/AggregatedView/
    // diffConfig is not required argument
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore

    aggregatedView.setNodes(nodes);
    const newModel = await aggregatedView.getModelAndWait(nodeToLoad);
    return newModel;
  }, [onLoadModelError]);
  const loadModelFromUrl = useCallback(async (modelUrn, aggregatedView) => {
    setLoading(true);
    const modelUrl = getModelUrl === null || getModelUrl === void 0 ? void 0 : getModelUrl(modelUrn);

    if (!modelUrl) {
      throw new Error(`Empty model url`);
    }

    await aggregatedView.viewer.loadModel(modelUrl, {
      skipPropertyDb: false
    }, // eslint-disable-next-line @typescript-eslint/no-empty-function
    res => {
      setViewerUrnMappingItem(res.getData().urn, modelUrn);
    }, err => {
      throw new Error(`Error loading model with urn:${modelUrl}, errorCode: ${err}`);
    });
  }, [getModelUrl]);
  useEffect(() => {
    const modelUrnFromQueue = Object.keys(modelStatus).find(key => modelStatus[key] === 'inQueue');

    if (!aggregatedView || !modelUrnFromQueue || loading) {
      return;
    }

    if (loadModelByUrl) {
      loadModelFromUrl(modelUrnFromQueue, aggregatedView);
    } else {
      loadModel(modelUrnFromQueue, aggregatedView);
    }
  }, [aggregatedView, loadModel, loadModelByUrl, loadModelFromUrl, loading, modelStatus]);
  return {
    geometryLoaded,
    invertedExternalIdMap
  };
}